import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiService } from './api.service';

/** Wraps @auth0/auth0-angular — the single auth entry point for the app. */
@Injectable({ providedIn: 'root' })
export class AuthFacadeService {
  private readonly auth = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly api = inject(ApiService);

  readonly email$: Observable<string | undefined> = this.auth.user$.pipe(
    map((u) => u?.email),
  );

  readonly isAuthenticated$ = this.auth.isAuthenticated$;
  readonly isLoading$ = this.auth.isLoading$;
  readonly user$ = this.auth.user$;

  /**
   * Errors surfaced from a redirect login — including a login blocked by the
   * post-login Action (e.g. unverified email). The SDK emits an
   * AuthenticationError carrying `error` ('access_denied') and
   * `error_description` (the message from `api.access.deny(...)`).
   */
  readonly loginError$: Observable<{
    code: string;
    description: string;
    needsVerification: boolean;
  } | null> = this.auth.error$.pipe(
    map((e) => {
      if (!e) return null;
      const err = e as { error?: string; error_description?: string };
      const code = err.error ?? 'error';
      // The post-login Action denies unverified users with the stable reason
      // 'email_unverified' (a code, not prose) so we can match it exactly —
      // access_denied alone can mean many things (blocked user, other rules…).
      const reason = err.error_description ?? e.message ?? '';
      const needsVerification =
        code === 'access_denied' && reason === 'email_unverified';
      const description = needsVerification
        ? 'Your email isn’t verified yet. Check your inbox for the verification link.'
        : reason || 'Sign-in failed. Please try again.';
      return { code, description, needsVerification };
    }),
  );
  readonly userId$: Observable<string | undefined> = this.auth.user$.pipe(
    map((u) => u?.sub),
  );

  /** The current Auth0 access token — handed to the capture extension over its
   *  secure channel so it can POST directly as the signed-in user. */
  getAccessToken(): Observable<string> {
    return this.auth.getAccessTokenSilently();
  }

  /** Key under which the attempted email is stashed across the login redirect. */
  static readonly PENDING_EMAIL_KEY = 'advocad_pending_login_email';

  login(target = '/', email?: string): void {
    // Survive the round-trip to Auth0 — if login is blocked (unverified email)
    // we come back without the email and need it to offer "resend".
    if (email) {
      try {
        sessionStorage.setItem(AuthFacadeService.PENDING_EMAIL_KEY, email);
      } catch {
        /* sessionStorage unavailable — non-fatal */
      }
    }
    this.auth.loginWithRedirect({
      appState: { target },
      authorizationParams: email ? { login_hint: email } : {},
    });
  }

  /** The email last used to attempt sign-in (set before the redirect). */
  pendingLoginEmail(): string {
    try {
      return sessionStorage.getItem(AuthFacadeService.PENDING_EMAIL_KEY) ?? '';
    } catch {
      return '';
    }
  }

  // One-shot hand-off of a login outcome from the app root to the login page.
  // Read-and-clear (`take*`) so a later in-app visit to /login can't re-trigger
  // a stale error (error$ is a ReplaySubject and would otherwise replay it).
  private _pendingVerifyEmail: string | null = null;
  private _pendingAuthError: string | null = null;

  flagUnverified(email: string): void {
    this._pendingVerifyEmail = email;
  }
  takeUnverified(): string | null {
    const v = this._pendingVerifyEmail;
    this._pendingVerifyEmail = null;
    return v;
  }
  flagAuthError(message: string): void {
    this._pendingAuthError = message;
  }
  takeAuthError(): string | null {
    const v = this._pendingAuthError;
    this._pendingAuthError = null;
    return v;
  }

  signup(target = '/set-profile', email?: string): void {
    this.auth.loginWithRedirect({
      appState: { target },
      authorizationParams: {
        screen_hint: 'signup',
        ...(email ? { login_hint: email } : {}),
      },
    });
  }

  logout(): void {
    this.auth.logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }

  /**
   * Create an account via our API, which creates the user in BOTH Auth0 (the
   * credential + verification email) and Mongo (the profile, seeded with the
   * first name) in one request. Auth0 then emails the verification link.
   */
  signupWithPassword(
    email: string,
    password: string,
    firstName: string,
  ): Observable<{ created: boolean }> {
    return this.api.post<{ created: boolean }>('/auth/signup', {
      firstName,
      email,
      password,
    });
  }

  /**
   * Re-send the email-verification link. Goes through our API because the
   * Auth0 Management API (jobs/verification-email) needs server-side M2M
   * credentials — the SPA can't call it directly. Public endpoint: the user
   * isn't signed in yet (their email is still unverified).
   */
  resendVerification(email: string): Observable<{ sent: boolean }> {
    return this.api.post<{ sent: boolean }>('/auth/resend-verification', {
      email,
    });
  }

  /** Trigger an Auth0 password-reset email (passwords are managed by Auth0). */
  sendPasswordReset(email: string): Observable<string> {
    return this.http.post(
      `https://${environment.auth0.domain}/dbconnections/change_password`,
      {
        client_id: environment.auth0.clientId,
        email,
        connection: 'Username-Password-Authentication',
      },
      { responseType: 'text' },
    );
  }
}
